import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { browserHistory } from 'react-router'
import { Pagination } from 'antd'
import { message } from '@surecompanies/core_components'

import { SimpleCustomerDto } from 'enrollment-platform/api/persons/dto/simple-customer.dto'
import { getMyClients } from 'enrollment-platform/api/persons'
import { getPaymentCarrierListNames } from '../../../api/payment-assignment-api-client'

import { getShortText } from '../../PaymentAssignment/Utils/payment-assignment.helper'

import {
  BenefitRow,
  BenefitSortKey,
  useBenefitSearch,
  useListBenefits,
} from '../../../api/reconciliation-v2-api-client'
import {
  ExplicitCreditReason,
  PullDayFilter,
} from '../../../api/reconciliation-v2-enums'

import ActivityLogDrawer from '../ActivityLogDrawer'
import AdjustPaymentModal, { AdjustPaymentTarget } from '../AdjustPaymentModal'
import { TableSkeleton, UpdatingBar } from '../Skeletons'
import { colors } from '../tokens'
import ZendeskTicketModal from '../ZendeskTicketModal'
import PrimaryPaymentMethodModal, {
  PrimaryPaymentMethodTarget,
} from '../PrimaryPaymentMethodModal'
import { useCanWriteReconciliation } from '../useCanWriteReconciliation'
import BenefitsToolbar from './BenefitsToolbar'
import BenefitTableRow from './BenefitTableRow'
import { exportBenefitsXlsx } from './exportBenefits'
import FiltersDrawer from './FiltersDrawer'
import { PrimaryPaymentMethodEditContext } from './PrimaryPaymentMethodCell'
import TableHead from './TableHead'
import { useCreditNoteSearch } from './useCreditNoteSearch'
import { replaceOwnedQuery } from '../replaceOwnedQuery'
import {
  ADJ_LINK,
  ADJ_LINK_KEYS,
  BENEFITS_URL_KEYS,
  buildBenefitLabel,
  buildCreditFilterParams,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  EMPLOYEE_COL_WIDTH,
  EXPECTED_COL_MIN_WIDTH,
  HideableColKey,
  HIDEABLE_COLUMNS,
  INFO_COLUMNS,
  INSURED_COL_WIDTH,
  isCreditFilterActive,
  isTestCompany,
  MONTH_COL_WIDTH,
  readInitialFilters,
  serializeCreditReasons,
  serializePullDays,
  ZENDESK_COL_WIDTH,
} from './helpers'
import styles from './BenefitsTable.module.scss'

/**
 * BenefitsTable — SureCo teal redesign (Table-Option1.dc.html).
 *
 * Hand-rolled <table> instead of Antd Table: the design pins TWO non-adjacent
 * columns (Insured Person at left:0 and Expected at left:198px) while the
 * hideable info columns between them scroll — Antd can only pin columns to the
 * table edges. Header is sticky-top; both pinned columns are sticky-left with
 * a 2px divider + soft right shadow.
 *
 * The table markup + styling has been split into TableHead / BenefitTableRow /
 * MonthCell, all sharing the one hashed BenefitsTable.module.scss so the sticky
 * columns, red warn cells and hover cascades keep matching across files. This
 * container keeps all state, data-fetching, URL sync, memos and handlers.
 */

const BenefitsTable: React.FC = () => {
  const initialQuery = (browserHistory.getCurrentLocation().query ||
    {}) as Record<string, string | undefined>
  const initialFilters = readInitialFilters(initialQuery)

  const [search, setSearch] = useState(initialFilters.search)
  const [appliedSearch, setAppliedSearch] = useState(initialFilters.search)
  const [page, setPage] = useState(initialFilters.page)
  const [limit, setLimit] = useState(25)
  const [year, setYear] = useState(initialFilters.year)
  const [carrierFilter, setCarrierFilter] = useState<string | undefined>(
    initialFilters.carrier
  )
  const [clients, setClients] = useState<SimpleCustomerDto[]>([])
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(
    initialFilters.clientId
  )
  const [sortBy, setSortBy] = useState<BenefitSortKey>(initialFilters.sortBy)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    initialFilters.sortOrder
  )
  const [emptyInMonth, setEmptyInMonth] = useState<string | undefined>(
    initialFilters.emptyInMonth
  )
  const [hasIssueFilter, setHasIssueFilter] = useState<boolean | undefined>(
    initialFilters.hasIssue
  )
  const [pullDays, setPullDays] = useState<PullDayFilter[]>(
    initialFilters.pullDays
  )
  const [hasExplicitCredit, setHasExplicitCredit] = useState<
    boolean | undefined
  >(initialFilters.hasExplicitCredit)
  const [creditReasons, setCreditReasons] = useState<ExplicitCreditReason[]>(
    initialFilters.creditReasons
  )
  const creditNote = useCreditNoteSearch(initialFilters.creditNote, () =>
    setPage(1)
  )
  const [showFullYear, setShowFullYear] = useState(initialFilters.showFullYear)
  const [showDetails, setShowDetails] = useState(true)
  const [hiddenCols, setHiddenCols] = useState<Set<HideableColKey>>(new Set())
  const [filtersOpen, setFiltersOpen] = useState(false)

  const [adjustTarget, setAdjustTarget] = useState<AdjustPaymentTarget | null>(
    null
  )

  const [exporting, setExporting] = useState(false)

  const { allowed: canWriteReconciliation } = useCanWriteReconciliation()
  const [ppmTarget, setPpmTarget] = useState<PrimaryPaymentMethodTarget | null>(
    null
  )
  const openEditPrimaryPaymentMethod = useCallback((row: BenefitRow) => {
    if (row.paymentAssignmentId == null) return
    setPpmTarget({
      benefitLabel: buildBenefitLabel(row),
      paymentAssignmentId: row.paymentAssignmentId,
    })
  }, [])

  // Inbound deep link from the Transaction Details tab: open the Adjust Payment
  // modal on a specific (benefit, coverage-month). Captured once on mount; the
  // benefit's display fields aren't in the URL (kept id-only to avoid leaking
  // names into history), so we resolve them via benefit-search below.
  const [adjDeepLink] = useState(() => {
    const q = (browserHistory.getCurrentLocation().query || {}) as Record<
      string,
      string | undefined
    >
    return {
      benefitId: q[ADJ_LINK.benefitId] || undefined,
      period: q[ADJ_LINK.period] || undefined,
      txn: q[ADJ_LINK.txn] || undefined,
    }
  })
  const [zendeskTarget, setZendeskTarget] = useState<{
    benefitId: string
    employeeName: string
  } | null>(null)
  const [activityTarget, setActivityTarget] = useState<{
    benefitId: string
    benefitLabel: string
    /** Cell history opens the drawer pre-filtered to this month (YYYY-MM-DD). */
    month?: string
  } | null>(null)

  // `isLoading` is true only on the very first load (no data yet);
  // background refetches keep prior data via keepPreviousData and only
  // flip `isFetching`, so we can leave the table on screen while updating.
  const { data, isFetching, isLoading, refetch } = useListBenefits({
    search: appliedSearch || undefined,
    carrier: carrierFilter,
    clientId: selectedClientId,
    hasIssue: hasIssueFilter,
    pullDays: pullDays.length > 0 ? pullDays : undefined,
    ...buildCreditFilterParams(
      hasExplicitCredit,
      creditReasons,
      creditNote.applied
    ),
    sortBy,
    sortOrder,
    emptyInMonth,
    year,
    page,
    limit,
  })

  useEffect(() => {
    getMyClients()
      .then((customers: SimpleCustomerDto[]) =>
        // Exclude test/mock companies from the picker. The shared
        // administered-customers list only carries the isTestClient flag (often
        // unset), so also drop name-pattern fakes ("ZZ Test Load", "…Demo") —
        // mirrors the reconciliation backend's test-company exclusion.
        setClients(customers.filter((c) => !isTestCompany(c)))
      )
      .catch(() => {
        /* non-fatal */
      })
  }, [])

  // Year the deep-linked allocation belongs to. benefit-search is year-scoped,
  // so resolve against the linked period's year — NOT this table's `year`
  // filter, which the operator may have moved somewhere else entirely. Matches
  // the scoping the Adjust modal applies to its own benefit picker.
  const adjDeepLinkYear = adjDeepLink.period
    ? Number(adjDeepLink.period.slice(0, 4))
    : NaN

  // Resolve the deep-linked benefit's display fields (label/insured/carrier)
  // that the id-only URL doesn't carry — same lookup BenefitSelect uses to
  // resolve a typed benefit id. Searching by id can still rank other benefits
  // ahead of the exact one, so pull a full page and pick the id out of it
  // rather than betting on the top hit.
  const { data: adjDeepLinkResults, isLoading: adjDeepLinkLoading } =
    useBenefitSearch(
      {
        search: adjDeepLink.benefitId,
        year: Number.isFinite(adjDeepLinkYear) ? adjDeepLinkYear : undefined,
        limit: 20,
      },
      { enabled: !!adjDeepLink.benefitId }
    )

  // Consume the inbound deep link once: build the Adjust Payment cell target
  // from the URL's (benefit, coverage-month), open the modal, then clear the
  // one-shot params so a later filter change / refresh doesn't reopen it.
  //
  // The resolver only supplies display fields; the modal loads the real cell
  // from (benefitId, periodStartDate) on its own. So an unresolved benefit
  // still opens — with an id-based label — instead of dropping the jump. A
  // genuinely bad id then surfaces inside the modal, where the operator can see
  // which cell failed, rather than as a toast on an untouched table.
  const consumedAdjDeepLink = useRef(false)
  useEffect(() => {
    if (consumedAdjDeepLink.current) return
    if (!adjDeepLink.benefitId || !adjDeepLink.period) {
      consumedAdjDeepLink.current = true
      return
    }
    if (adjDeepLinkLoading) return // wait for the resolver
    const match = adjDeepLinkResults?.find(
      (r) => r.benefitId === adjDeepLink.benefitId
    )
    consumedAdjDeepLink.current = true
    setAdjustTarget({
      benefitId: adjDeepLink.benefitId,
      periodStartDate: adjDeepLink.period,
      benefitLabel: match
        ? buildBenefitLabel(match)
        : `Benefit ${getShortText(adjDeepLink.benefitId, 4)}`,
      employeePublicKey: match?.employeePublicKey ?? null,
      insuredPersons: match?.insuredPersons ?? [],
      carrierName: match?.carrierName ?? null,
      preselectTransactionId: adjDeepLink.txn,
    })
    replaceOwnedQuery(ADJ_LINK_KEYS, {})
  }, [adjDeepLink, adjDeepLinkResults, adjDeepLinkLoading])

  // Reflect the active filters in the URL so views are shareable. Skips the
  // first run so we don't rewrite/normalize an incoming shared link on mount;
  // uses replace (not push) so filter tweaks don't spam browser history.
  // Merge around BENEFITS_URL_KEYS so Transaction Details `td*` params and
  // `tab` survive a Benefits filter change.
  const isFirstUrlSync = useRef(true)
  useEffect(() => {
    if (isFirstUrlSync.current) {
      isFirstUrlSync.current = false
      return
    }
    const query: Record<string, string> = { year: String(year) }
    if (appliedSearch) query.q = appliedSearch
    if (selectedClientId) query.clientId = selectedClientId
    if (carrierFilter) query.carrier = carrierFilter
    if (emptyInMonth) query.emptyInMonth = emptyInMonth
    if (showFullYear) query.fullYear = 'true'
    if (hasIssueFilter === true) query.hasIssue = 'true'
    const pullDaysParam = serializePullDays(pullDays)
    if (pullDaysParam) query.pullDays = pullDaysParam
    if (hasExplicitCredit !== undefined) {
      query.hasExplicitCredit = String(hasExplicitCredit)
    }
    const creditReasonsParam = serializeCreditReasons(creditReasons)
    if (creditReasonsParam) query.creditReasons = creditReasonsParam
    if (creditNote.applied) query.creditNote = creditNote.applied
    if (sortBy !== DEFAULT_SORT_BY) query.sortBy = sortBy
    if (sortOrder !== DEFAULT_SORT_ORDER) query.sortOrder = sortOrder
    if (page > 1) query.page = String(page)
    replaceOwnedQuery(BENEFITS_URL_KEYS, query)
  }, [
    appliedSearch,
    selectedClientId,
    carrierFilter,
    year,
    showFullYear,
    emptyInMonth,
    hasIssueFilter,
    pullDays,
    hasExplicitCredit,
    creditReasons,
    creditNote.applied,
    sortBy,
    sortOrder,
    page,
  ])

  const visibleMonthIndices = useMemo(() => {
    if (showFullYear) return Array.from({ length: 12 }, (_, i) => i)
    const today = new Date()
    const currentYear = today.getFullYear()
    if (year > currentYear) return [] // future year, nothing yet
    if (year < currentYear) return Array.from({ length: 12 }, (_, i) => i)
    // Default view shows months through NEXT month (current + 1) so the
    // upcoming period is visible. In December, +1 rolls into January of the
    // next year — which doesn't exist in this single-year view — so cap the
    // range at 12 (Jan–Dec) rather than producing an out-of-range month index.
    return Array.from(
      { length: Math.min(today.getMonth() + 2, 12) },
      (_, i) => i
    )
  }, [year, showFullYear])

  // Carrier filter options come from the full carrier list rather than only the
  // carriers on the currently-loaded page, so every carrier stays selectable
  // regardless of which benefits happen to be in view.
  const [carrierNames, setCarrierNames] = useState<string[]>([])

  useEffect(() => {
    getPaymentCarrierListNames()
      .then((names) => setCarrierNames(names))
      .catch(() => {
        /* non-fatal */
      })
  }, [])

  const carrierOptions = useMemo(
    () =>
      Array.from(new Set(carrierNames.filter(Boolean)))
        .sort()
        .map((c) => ({ label: c, value: c })),
    [carrierNames]
  )

  const filteredData = data?.data ?? []

  const toggleCol = (key: HideableColKey) =>
    setHiddenCols((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const toggleAllCols = () =>
    setHiddenCols((prev) =>
      prev.size > 0
        ? new Set<HideableColKey>()
        : new Set(HIDEABLE_COLUMNS.map((c) => c.key))
    )

  const openAdjust = (row: BenefitRow, periodKey: string) => {
    setAdjustTarget({
      benefitId: row.benefitId,
      periodStartDate: periodKey,
      benefitLabel: buildBenefitLabel(row),
      employeePublicKey: row.employeePublicKey,
      insuredPersons: row.insuredPersons,
      carrierName: row.carrierName,
    })
  }

  const openActivity = (row: BenefitRow) =>
    setActivityTarget({
      benefitId: row.benefitId,
      benefitLabel: buildBenefitLabel(row),
    })

  const applySearch = (v: string) => {
    setAppliedSearch(v)
    setPage(1)
  }

  // 3-state cycle per header click: asc → desc → default (EmployeeName asc,
  // mirroring the old Antd onChange reset).
  const cycleSort = (key: BenefitSortKey) => {
    if (sortBy !== key) {
      setSortBy(key)
      setSortOrder('asc')
    } else if (sortOrder === 'asc') {
      setSortOrder('desc')
    } else {
      setSortBy(BenefitSortKey.EmployeeName)
      setSortOrder('asc')
    }
  }

  const sortDir = (key: BenefitSortKey): 'asc' | 'desc' | null =>
    sortBy === key ? sortOrder : null

  // Active data-filter count for the Filters button badge. `year` only counts
  // when it differs from the current year, so a pristine load shows no badge.
  const activeFilterCount =
    (selectedClientId ? 1 : 0) +
    (carrierFilter ? 1 : 0) +
    (emptyInMonth ? 1 : 0) +
    (hasIssueFilter !== undefined ? 1 : 0) +
    (pullDays.length > 0 ? 1 : 0) +
    (isCreditFilterActive(hasExplicitCredit, creditReasons, creditNote.applied)
      ? 1
      : 0) +
    (year !== new Date().getFullYear() ? 1 : 0)

  // Changing the table year must keep `emptyInMonth` (a YYYY-MM-DD period key)
  // consistent with the new year — otherwise the list API keeps filtering on
  // the previous year's month while the columns show the new year. If a month
  // filter is active, roll it to the same month in the new year; otherwise it
  // stays cleared. Also reset pagination to page 1, matching the other filters.
  const handleYearChange = (updater: (y: number) => number) => {
    const nextYear = updater(year)
    setYear(nextYear)
    setEmptyInMonth((prev) => (prev ? `${nextYear}${prev.slice(4)}` : prev))
    setPage(1)
  }

  const clearAllFilters = () => {
    setSelectedClientId(undefined)
    setCarrierFilter(undefined)
    setEmptyInMonth(undefined)
    setHasIssueFilter(undefined)
    setPullDays([])
    setHasExplicitCredit(undefined)
    setCreditReasons([])
    creditNote.clear()
    setYear(new Date().getFullYear())
    setShowFullYear(false)
    setPage(1)
  }

  // Server-side pagination means "Export" covers the currently loaded page
  // only (mirrors the Transaction Details tab). Builds the styled XLSX from the
  // rows already in memory over the on-screen visible months — no extra
  // requests. A full-dataset export would need to walk every page and is
  // deferred.
  const handleExport = async () => {
    if (exporting) return
    const rows = data?.data ?? []
    if (rows.length === 0) {
      message.warning('No benefits to export.')
      return
    }
    setExporting(true)
    try {
      await exportBenefitsXlsx({ rows, visibleMonthIndices, year })
    } catch {
      message.error('Failed to export benefits. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  const paginationTotal = data?.total ?? 0

  const employeeVisible = !hiddenCols.has('employeeName')
  const visibleInfoCols = INFO_COLUMNS.filter((c) => !hiddenCols.has(c.key))

  const tableMinWidth =
    INSURED_COL_WIDTH +
    EXPECTED_COL_MIN_WIDTH +
    ZENDESK_COL_WIDTH +
    (employeeVisible ? EMPLOYEE_COL_WIDTH : 0) +
    visibleInfoCols.reduce((s, c) => s + c.minWidth, 0) +
    visibleMonthIndices.length * MONTH_COL_WIDTH

  const visibleColCount =
    6 + // insured + expected + sum-exp + sum-paid + diff + zendesk
    (employeeVisible ? 1 : 0) +
    visibleInfoCols.length +
    visibleMonthIndices.length

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      <BenefitsToolbar
        search={search}
        onSearchChange={setSearch}
        onApplySearch={applySearch}
        appliedSearch={appliedSearch}
        activeFilterCount={activeFilterCount}
        filtersOpen={filtersOpen}
        onOpenFilters={() => setFiltersOpen(true)}
        isFetching={isFetching}
        onRefresh={() => refetch()}
        showDetails={showDetails}
        onToggleDetails={() => setShowDetails((v) => !v)}
        onExport={handleExport}
        exporting={exporting}
      />

      {/* Full skeleton only on the very first load. Background refetches keep
          the current rows on screen and show the subtle updating bar instead. */}
      {isLoading ? (
        <TableSkeleton rowCount={Math.min(limit, 10)} />
      ) : (
        <>
          <UpdatingBar $active={isFetching} />
          <div className={styles.scrollBox}>
            <table className={styles.tbl} style={{ minWidth: tableMinWidth }}>
              <TableHead
                employeeVisible={employeeVisible}
                visibleInfoCols={visibleInfoCols}
                visibleMonthIndices={visibleMonthIndices}
                sortDir={sortDir}
                onSort={cycleSort}
                hiddenCols={hiddenCols}
                onToggleCol={toggleCol}
                onToggleAll={toggleAllCols}
              />
              <PrimaryPaymentMethodEditContext.Provider
                value={
                  canWriteReconciliation ? openEditPrimaryPaymentMethod : null
                }
              >
                <tbody>
                  {filteredData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={visibleColCount}
                        style={{
                          textAlign: 'center',
                          padding: '48px 16px',
                          color: colors.text.faint,
                        }}
                      >
                        No benefits match the current filters.
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((row) => (
                      <BenefitTableRow
                        key={row.benefitId}
                        row={row}
                        year={year}
                        showDetails={showDetails}
                        employeeVisible={employeeVisible}
                        visibleInfoCols={visibleInfoCols}
                        visibleMonthIndices={visibleMonthIndices}
                        onAdjust={openAdjust}
                        onOpenActivity={setActivityTarget}
                        onOpenRowActivity={openActivity}
                        onOpenZendesk={setZendeskTarget}
                      />
                    ))
                  )}
                </tbody>
              </PrimaryPaymentMethodEditContext.Provider>
            </table>
          </div>

          <div className={styles.pagBar}>
            <Pagination
              total={paginationTotal}
              current={page}
              pageSize={limit}
              showSizeChanger
              pageSizeOptions={[25, 50, 100]}
              onChange={(p, ps) => {
                setPage(p)
                setLimit(ps)
              }}
              showTotal={(total, [start, end]) => `${start}–${end} of ${total}`}
            />
          </div>
        </>
      )}

      <AdjustPaymentModal
        open={!!adjustTarget}
        onClose={() => setAdjustTarget(null)}
        target={adjustTarget}
      />
      <ZendeskTicketModal
        open={!!zendeskTarget}
        onClose={() => setZendeskTarget(null)}
        benefitId={zendeskTarget?.benefitId ?? null}
        periodStartDate={null}
        employeeName={zendeskTarget?.employeeName}
      />
      <PrimaryPaymentMethodModal
        target={ppmTarget}
        onClose={() => setPpmTarget(null)}
        onSaved={() => refetch()}
      />
      <ActivityLogDrawer
        open={!!activityTarget}
        onClose={() => setActivityTarget(null)}
        benefitId={activityTarget?.benefitId ?? null}
        benefitLabel={activityTarget?.benefitLabel}
        initialMonth={activityTarget?.month}
      />

      <FiltersDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        clients={clients}
        selectedClientId={selectedClientId}
        onClientChange={(v) => {
          setSelectedClientId(v)
          setPage(1)
        }}
        carrierFilter={carrierFilter}
        carrierOptions={carrierOptions}
        onCarrierChange={(v) => {
          setCarrierFilter(v)
          setPage(1)
        }}
        emptyInMonth={emptyInMonth}
        onEmptyInMonthChange={(v) => {
          setEmptyInMonth(v)
          setPage(1)
        }}
        year={year}
        onYearChange={handleYearChange}
        showFullYear={showFullYear}
        onToggleFullYear={() => setShowFullYear((v) => !v)}
        hasIssueFilter={hasIssueFilter}
        onToggleHasIssue={() => {
          setHasIssueFilter((v) => (v === true ? undefined : true))
          setPage(1)
        }}
        pullDays={pullDays}
        onPullDaysChange={(v) => {
          setPullDays(v)
          setPage(1)
        }}
        hasExplicitCredit={hasExplicitCredit}
        onHasExplicitCreditChange={(v) => {
          setHasExplicitCredit(v)
          // Reason and note narrow "has explicit credit" and the drawer hides
          // them otherwise, so leaving it clears both.
          if (v !== true) {
            setCreditReasons([])
            creditNote.clear()
          }
          setPage(1)
        }}
        creditReasons={creditReasons}
        onCreditReasonsChange={(v) => {
          setCreditReasons(v)
          setPage(1)
        }}
        creditNote={creditNote.draft}
        onCreditNoteChange={creditNote.setDraft}
        onClearAll={clearAllFilters}
      />
    </div>
  )
}

export default BenefitsTable
